import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import {
  Bell, CheckCheck, X, HardHat, Wallet, FileText, CheckCircle2, XCircle,
  Wrench, Sparkles, Users,
} from "lucide-react";

const ICON_MAP = {
  installation_completed: HardHat,
  sc_payout_status: Wallet,
  quotation_sent: FileText,
  quotation_accepted: CheckCircle2,
  quotation_rejected: XCircle,
  ticket_status: Wrench,
  ticket_assigned: Wrench,
  sc_new_lead: Sparkles,
  lead_assigned: Users,
};

/** Generic notification bell + slide-in sheet — reused across CRM, SC App and
 * Customer App. Any authenticated user's own notifications are scoped
 * server-side, so this component needs no role-specific props. */
export default function NotificationBell({ iconClassName = "w-4 h-4", buttonClassName = "" }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const refreshCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.unread || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    refreshCount();
    const t = setInterval(refreshCount, 30000);
    const onVisible = () => { if (document.visibilityState === "visible") refreshCount(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVisible); };
  }, [refreshCount]);

  async function openSheet() {
    setOpen(true);
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } finally {
      setLoading(false);
    }
  }

  async function readAll() {
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch { /* silent */ }
  }

  async function onItemClick(n) {
    if (!n.read) {
      try {
        await api.put(`/notifications/${n.id}/read`);
        setItems((prev) => prev.map((it) => (it.id === n.id ? { ...it, read: true } : it)));
        setUnread((u) => Math.max(0, u - 1));
      } catch { /* silent */ }
    }
    if (n.link) {
      setOpen(false);
      nav(n.link);
    }
  }

  return (
    <>
      <button
        onClick={openSheet}
        data-testid="notification-bell"
        className={"relative p-2 " + (buttonClassName || "text-slate-500 hover:text-emerald-700")}
        aria-label="Notifications"
      >
        <Bell className={iconClassName} />
        {unread > 0 && (
          <span
            data-testid="notification-unread-dot"
            className="absolute top-0.5 right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && createPortal((
        <>
          <div
            className="fixed inset-0 z-[60] bg-black/40"
            onClick={() => setOpen(false)}
            data-testid="notifications-backdrop"
          />
          <div
            className="fixed right-0 top-0 bottom-0 z-[61] w-full max-w-md bg-white shadow-xl flex flex-col"
            data-testid="notifications-sheet"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <div>
                <div className="text-[9px] font-medium uppercase tracking-[0.18em] text-amber-600">Notifications</div>
                <div className="font-display font-bold text-slate-900">Updates for you</div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={readAll}
                  disabled={items.every((i) => i.read)}
                  data-testid="notifications-read-all"
                  className="text-[10px] font-medium uppercase tracking-wider text-emerald-700 hover:text-emerald-800 disabled:text-slate-300 px-2 py-1 flex items-center gap-1"
                >
                  <CheckCheck className="w-3 h-3" /> Mark all read
                </button>
                <button
                  onClick={() => setOpen(false)}
                  data-testid="notifications-close"
                  className="p-1.5 text-slate-500 hover:text-slate-900"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-3 bg-slate-50">
              {loading && <div className="text-xs text-slate-500 text-center py-8">Loading…</div>}
              {!loading && items.length === 0 && (
                <div className="text-center py-16 text-sm text-slate-500">
                  <Bell className="w-8 h-8 mx-auto text-slate-300" />
                  <div className="mt-3">No notifications yet.</div>
                </div>
              )}
              {items.map((n) => {
                const Icon = ICON_MAP[n.type] || Bell;
                return (
                  <button
                    key={n.id}
                    onClick={() => onItemClick(n)}
                    data-testid={`notification-${n.id}`}
                    className={
                      "w-full text-left mb-2 p-3 rounded-lg border transition-all block " +
                      (n.read ? "bg-white border-slate-200 opacity-70" : "bg-emerald-50 border-emerald-200 shadow-sm")
                    }
                  >
                    <div className="flex items-start gap-2.5">
                      <div className="p-1.5 rounded-full flex-shrink-0 bg-emerald-600">
                        <Icon className="w-3.5 h-3.5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-slate-900 leading-tight">{n.title}</div>
                        {n.body && <div className="text-[11px] text-slate-600 mt-1 leading-tight">{n.body}</div>}
                        <div className="text-[10px] text-slate-400 mt-1.5 font-mono">
                          {new Date(n.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                      {!n.read && <span className="w-2 h-2 rounded-full bg-red-500 mt-1 flex-shrink-0" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      ), document.body)}
    </>
  );
}
